// Region map painter for the World map page: one painted overworld per region, in the same pixel
// style as the hunt maps and creatures (five-step ramps, light from the upper left, a near-black
// outline around things that stand up).
//
// The map page only gives the area points (0..1 of the map box). Everything else is derived:
// - land is an island around the points, with the trail between areas always on land;
// - each patch of land takes the biome of its nearest area (the same biome its hunt map uses),
//   with ground colours from terrain.ts, so the overworld matches the hunts you open from it;
// - each biome scatters its own landmarks: pines, firs, olives, cypresses, bamboo, cherry trees,
//   dead trees, mountains, volcanoes, columns, houses, torii, graves, lakes, rivers, lava;
// - areas sit in small clearings so the area rings and labels stay readable.
//
// Painted once per region and map-box size (one map pixel = MAP_SCALE CSS pixels) and cached.
import { vnoise, hashString, biomeFor } from '../shared/worldmap.ts';
import { AREAS } from '../shared/grounds.ts';
import { R } from '../art/rig3d/palette.ts';
import { STYLE } from '../art/rig3d/style.ts';
import { biomeLook } from './terrain.ts';

/** CSS pixels per map pixel. */
export const MAP_SCALE = 3;

/** An area point, 0..1 of the map box. */
export interface MapPoint { id: string; x: number; y: number }

type RGB = [number, number, number];
type Ramp = readonly string[];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const mixc = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const bayer = (x: number, y: number) => (BAYER[(y & 3) * 4 + (x & 3)] + 0.5) / 16;
function fbm(seed: number, x: number, y: number) {
  return vnoise(seed, x, y) * 0.6 + vnoise(seed + 17, x * 2.1, y * 2.1) * 0.28 + vnoise(seed + 31, x * 4.3, y * 4.3) * 0.12;
}
function hash(seed: number, x: number, y: number) {
  let h = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(seed, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => { s = Math.imul(s ^ (s >>> 15), 2246822507) + 0x9e3779b9 >>> 0; s ^= s >>> 13; return (s >>> 0) / 4294967296; };
}

/** Sea ramps, deep to foam. */
const SEA: Record<string, Ramp> = {
  midgard: ['#08162e', '#10305a', '#1c5288', '#4486bc', '#bfe4f6'],
  hellas: ['#0a2246', '#16447e', '#2472b2', '#4ea6d8', '#d6f4ff'],
  yomi: ['#0a0a20', '#14183c', '#202a5a', '#3a4a84', '#a4b4e0'],
};
/** The band of land along the coast: [edge, inner]. */
const SHORE: Record<string, [string, string]> = {
  midgard: ['#3a4268', '#8a96c0'],
  hellas: ['#c4a87e', '#ead6a4'],
  yomi: ['#2a2a38', '#5a5664'],
};

// ---------------- sprites ----------------
/** A small sprite: palette indices (0 = empty) over a palette. */
interface Spr { w: number; h: number; px: Uint8Array; pal: RGB[] }
function spr(w: number, h: number, pal: Ramp | string[]): Spr { return { w, h, px: new Uint8Array(w * h), pal: [[0, 0, 0], ...pal.map(hex)] }; }
const set = (s: Spr, x: number, y: number, i: number) => { if (x >= 0 && y >= 0 && x < s.w && y < s.h) s.px[y * s.w + x] = i; };

/** Conifer: stacked drooping tiers, lit on the left; snowy ones carry snow on each tier. */
function conifer(r: () => number, needle: Ramp, snow: Ramp | null, tall = 0): Spr {
  const h = 8 + Math.floor(r() * 3) + tall, w = h >= 10 ? 7 : 5, cx = (w - 1) / 2;
  // palette: 1-4 needle dark..light, 5-6 snow shade/light, 7 trunk
  const s = spr(w, h, [needle[0], needle[1], needle[2], needle[3], snow ? snow[2] : needle[3], snow ? snow[4] : needle[4], R.bark[1]]);
  const tiers = 3, body = h - 1;
  for (let y = 0; y < body; y++) {
    const t = Math.min(tiers - 1, Math.floor((y / body) * tiers));
    const ty = y - Math.floor((t * body) / tiers);
    const hw = Math.min(cx, Math.floor(t * 0.75 + ty * 0.7));
    for (let x = cx - hw; x <= cx + hw; x++) {
      let i = x < cx ? 3 : x === cx ? 2 : 1;
      if (x === cx - hw && hw > 0) i = 4;
      if (ty === 0 || (y === 0)) i = x <= cx ? 4 : 2;
      set(s, x, y, i);
    }
    // the tier's last row hangs dark on the right
    const last = Math.floor(((t + 1) * body) / tiers) - 1;
    if (y === last) for (let x = cx + 1; x <= cx + hw; x++) set(s, x, y, 1);
    if (snow && ty === 0) for (let x = cx - hw; x <= cx; x++) set(s, x, y, x === cx - hw ? 5 : 6);
    if (snow && ty === 1 && hw > 1) set(s, cx - hw, y, 6);
  }
  set(s, cx, h - 1, 7);
  return s;
}

/** Round canopy tree (olive, oak, cherry): a shaded ball on a short trunk. */
function roundTree(r: () => number, leaf: Ramp, bark: Ramp = R.bark): Spr {
  const rad = 2.4 + r() * 1.2, w = Math.ceil(rad * 2) + 1, h = w + 2, cx = (w - 1) / 2, cy = rad + 0.3;
  const s = spr(w, h, [leaf[0], leaf[1], leaf[2], leaf[3], leaf[4], bark[1], bark[2]]);
  for (let y = 0; y < h - 2; y++) for (let x = 0; x < w; x++) {
    const dx = (x - cx) / rad, dy = (y - cy) / rad, d = dx * dx + dy * dy;
    if (d > 1) continue;
    const l = -(dx * -0.6 + dy * -0.8) - (1 - d) * 0.4; // lower = lit
    const i = l < -0.55 ? 4 : l < -0.05 ? 3 : l < 0.45 ? 2 : 1;
    set(s, x, y, (x + y) % 3 === 0 && i === 3 && r() < 0.5 ? 4 : i);
  }
  const tx = Math.round(cx);
  set(s, tx, h - 2, 6); set(s, tx, h - 1, 6); set(s, tx + 1, h - 1, 7);
  return s;
}

/** Cypress: a tall dark flame of a tree. */
function cypress(r: () => number, leaf: Ramp): Spr {
  const h = 9 + Math.floor(r() * 3), w = 3, s = spr(w, h, [leaf[0], leaf[1], leaf[2], leaf[3], R.bark[1]]);
  for (let y = 0; y < h - 1; y++) {
    const f = y / (h - 2), hw = y === 0 ? 0 : f < 0.2 || f > 0.92 ? 0.5 : 1;
    for (let x = 1 - Math.floor(hw); x <= 1 + Math.floor(hw); x++) set(s, x, y, x === 0 ? 3 : x === 1 ? (y % 3 === 1 ? 4 : 2) : 1);
    if (hw === 0.5) set(s, 1, y, 2);
  }
  set(s, 1, h - 1, 5);
  return s;
}

/** Dead tree: a crooked trunk and a few bare branches. */
function deadTree(r: () => number, bark: Ramp): Spr {
  const h = 7 + Math.floor(r() * 3), w = 5, s = spr(w, h, [bark[0], bark[1], bark[2], bark[3]]);
  for (let y = 2; y < h; y++) set(s, 2, y, y < h - 2 ? 2 : 1);
  set(s, 2, 1, 3); set(s, 1, 2, 3); set(s, 0, 1, 3); set(s, 3, 3, 2); set(s, 4, 2, 2);
  if (r() < 0.5) { set(s, 1, 4, 3); set(s, 0, 3, 3); }
  set(s, 3, h - 1, 1);
  return s;
}

/** A clump of bamboo: thin jointed stalks, a spray of leaves at each top. */
function bamboo(r: () => number, leaf: Ramp): Spr {
  const h = 9 + Math.floor(r() * 3), w = 6, s = spr(w, h, [leaf[0], leaf[1], leaf[2], leaf[3], leaf[4]]);
  for (const [x, top] of [[1, 2], [3, 0], [4, 3]] as const) {
    const t = top + Math.floor(r() * 2);
    for (let y = t + 1; y < h; y++) set(s, x, y, (y - t) % 3 === 0 ? 4 : x === 1 ? 3 : 2);
    set(s, x - 1, t, 3); set(s, x, t, 4); set(s, x + 1, t + 1, 2);
  }
  set(s, 0, h - 1, 1); set(s, 5, h - 1, 1);
  return s;
}

/** Mountain: a lit left face, a shadowed right face split at a ridge, a cap of snow (or a crater). */
function mountain(r: () => number, w: number, h: number, rock: Ramp, cap: Ramp | null, lava = false): Spr {
  const s = spr(w, h, [rock[0], rock[1], rock[2], rock[3], cap ? cap[2] : rock[3], cap ? cap[4] : rock[4], R.lava[2], R.lava[4]]);
  const peak = Math.floor(w * (0.4 + r() * 0.2));
  const jag = Array.from({ length: w }, () => r());
  for (let x = 0; x < w; x++) {
    const side = x <= peak ? x / Math.max(1, peak) : (w - 1 - x) / Math.max(1, w - 1 - peak);
    let top = Math.round((1 - side) * (h - 1) + (jag[x] - 0.5) * 1.6);
    top = Math.max(lava ? 1 : 0, Math.min(h - 1, top));
    // the ridge runs from the peak down to the right
    const ridge = peak + Math.floor((h - 1 - top) * 0.25);
    for (let y = top; y < h; y++) {
      const depth = (y - top) / Math.max(1, h - top);
      let i = x < ridge ? (x === 0 || y === top ? 4 : 3) : x === ridge ? 2 : 1;
      if (x >= ridge && y === top) i = 2;
      if (y >= h - 2 && x >= ridge) i = 1;
      const capLine = h * (0.32 + jag[(x + 3) % w] * 0.12);
      if (cap && y < capLine) i = x < ridge ? 6 : 5;
      if (!cap && depth > 0.55 && x < ridge && (x + y) % 4 === 0) i = 2;
      set(s, x, y, i);
    }
  }
  if (lava) { // a glowing crater and one lava run down the lit face
    for (let x = peak - 1; x <= peak + 1; x++) set(s, x, 1, 8);
    set(s, peak, 1, 7);
    for (let y = 2, x = peak - 1; y < h - 1; y++, x -= (y % 2)) set(s, x, y, y < h * 0.6 ? 8 : 7);
  }
  return s;
}

/** Low hill: a dome lit from the upper left. */
function hill(r: () => number, g: Ramp): Spr {
  const w = 9 + Math.floor(r() * 4), h = 4 + Math.floor(r() * 2), s = spr(w, h, [g[1], g[2], g[3], g[4]]);
  for (let x = 0; x < w; x++) {
    const t = (x - (w - 1) / 2) / ((w - 1) / 2), top = Math.round((1 - Math.sqrt(Math.max(0, 1 - t * t))) * (h - 1));
    for (let y = top; y < h; y++) set(s, x, y, y === top ? (t < 0.2 ? 4 : 2) : t < -0.2 ? 3 : t < 0.35 ? 2 : 1);
  }
  return s;
}

/** Hand-placed tiny things: rows of palette indices. */
function rows(lines: string[], pal: string[]): Spr {
  const w = Math.max(...lines.map((l) => l.length)), s = spr(w, lines.length, pal);
  lines.forEach((l, y) => [...l].forEach((c, x) => { if (c !== '.') set(s, x, y, Number(c)); }));
  return s;
}
const COLUMN = () => rows(['333', '.3.', '.31', '.31', '.31', '2221'], [R.marble[1], R.marble[2], R.marble[3]]);
const COLUMN_BROKEN = () => rows(['.3.', '.31', '.31', '2221'], [R.marble[1], R.marble[2], R.marble[3]]);
const TEMPLE = () => rows(['..4444..', '.444444.', '33333333', '.3.3.3.2', '.3.3.3.2', '22222221'], [R.marble[0], R.marble[1], R.marble[2], R.terracotta[2]]);
const HOUSE_HELLAS = () => rows(['.444.', '44444', '33332', '31.32', '33322'], [R.cream[0], R.cream[2], R.cream[3], R.terracotta[2]]);
const LONGHOUSE = () => rows(['..44...', '.44445.', '4444455', '3331332', '3331332'], [R.bark[1], R.bark[2], R.bark[3], R.bark[0], R.snowCap[3]]);
const HOUSE_YOMI = () => rows(['.44444.', '4444444', '.32332.', '.31331.'], [R.bark[1], R.cream[1], R.cream[2], R.charcoal[1]]);
const TORII = () => rows(['4444444', '.3...2.', '3333332', '.3...2.', '.3...2.'], [R.oniRed[0], R.oniRed[1], R.oniRed[2], R.oniRed[3]]);
const GRAVE = () => rows(['.3.', '332', '322', '222'], [R.stone[1], R.stone[2], R.stone[3]]);
const RUNESTONE = () => rows(['.3.', '332', '342', '322', '222'], [R.stone[1], R.stone[2], R.stone[3], R.runeGlow[2]]);
const LANTERN = () => rows(['22', '43', '22', '.1'], [R.charcoal[1], R.charcoal[2], R.gold[3], R.gold[4]]);
const REEDS = () => rows(['2..2.', '2.23.', '.2323', '12121'], ['#4a4a24', '#8a844a', '#b8ae6a']);
const BAMBOO: Ramp = ['#0a2016', '#1a4228', '#2e6638', '#548a4e', '#8cb87a'];
const SPIRE = () => rows(['.3.', '.32', '332', '3221', '32211'], [R.basalt[1], R.basalt[2], R.basalt[3]]);

// Named landmarks for single areas, drawn beside their map token: Ironwood's iron-oaks, the Bifrost's
// rainbow bridge, the mouth of the Nidavellir mines, the steaming pool of Hvergelmir.
const IRON_LEAF: Ramp = ['#0c1618', '#1a2c2e', '#2c4442', '#47625a', '#6f8c80'], IRON_BARK: Ramp = ['#14161c', '#2a2e36', '#4a505a', '#707782', '#a0a8b2'];
const BIFROST = (): Spr => {
  const w = 17, h = 9, s = spr(w, h, ['#a8463c', '#cc9448', '#d6c666', '#5a9858', '#4a74b4', '#7c58b0']);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const r = Math.hypot(x - 8, (y - 8.6) * 1.05), band = Math.floor(8.4 - r);
    if (band >= 0 && band < 6) set(s, x, y, band + 1);
  }
  return s;
};
const MINE = () => rows(['...5455...', '..544445..', '.43333334.', '.42111124.', '442111124.', '4421111244'], ['#120c0a', '#5a3c22', '#8a6236', '#55565e', '#8a8c96']);
const SPRING = () => rows(['...5.5...', '....5....', '...5.....', '..34443..', '.3122213.', '.3211123.', '..33333..'], ['#1c4a6e', '#5aa6d2', '#5c6068', '#8e939c', '#eef4fa']);
const AREA_MARKS: Record<string, (r: () => number) => Spr[]> = {
  'midgard-16': (r) => [roundTree(r, IRON_LEAF, IRON_BARK), roundTree(r, IRON_LEAF, IRON_BARK), roundTree(r, IRON_LEAF, IRON_BARK)],
  'midgard-17': () => [BIFROST()],
  'midgard-18': () => [MINE()],
  'midgard-19': () => [SPRING()],
};

type Kind = 'pine' | 'fir' | 'olive' | 'oak' | 'cypress' | 'sakura' | 'bamboo' | 'dead' | 'mtn' | 'ice' | 'volcano' | 'hill' | 'column' | 'broken'
  | 'temple' | 'house' | 'longhouse' | 'pagoda' | 'torii' | 'grave' | 'runestone' | 'lantern' | 'reeds' | 'spire';
const BIG: Kind[] = ['mtn', 'ice', 'volcano', 'hill', 'temple'];

function makeSprite(k: Kind, r: () => number, g: Ramp): Spr {
  switch (k) {
    case 'pine': return conifer(r, R.pine, R.snowCap);
    case 'fir': return conifer(r, R.pine, null);
    case 'olive': return roundTree(r, R.moss);
    case 'oak': return roundTree(r, R.leaf);
    case 'cypress': return cypress(r, R.pine);
    case 'sakura': return roundTree(r, R.pink, R.bark);
    case 'bamboo': return bamboo(r, BAMBOO);
    case 'dead': return deadTree(r, R.bark);
    case 'mtn': return mountain(r, 13 + Math.floor(r() * 6), 10 + Math.floor(r() * 4), R.stone, R.snowCap);
    case 'ice': return mountain(r, 13 + Math.floor(r() * 5), 10 + Math.floor(r() * 3), ['#1e2850', '#3e5288', '#6a86bc', '#a4c0e4', '#e6f4ff'], R.ice);
    case 'volcano': return mountain(r, 15 + Math.floor(r() * 4), 11 + Math.floor(r() * 3), R.basalt, null, true);
    case 'hill': return hill(r, g);
    case 'column': return COLUMN();
    case 'broken': return COLUMN_BROKEN();
    case 'temple': return TEMPLE();
    case 'house': return HOUSE_HELLAS();
    case 'longhouse': return LONGHOUSE();
    case 'pagoda': return HOUSE_YOMI();
    case 'torii': return TORII();
    case 'grave': return GRAVE();
    case 'runestone': return RUNESTONE();
    case 'lantern': return LANTERN();
    case 'reeds': return REEDS();
    case 'spire': return SPIRE();
  }
}

/** What grows and stands in each biome: big landmarks, then small ones; density 0..1; liquid on land. */
interface Recipe { big?: [Kind, number][]; bigDensity?: number; small: [Kind, number][]; density: number; liquid?: 'lake' | 'river' | 'lava' | 'puddle' }
const RECIPE: Record<string, Recipe> = {
  snow: { small: [['pine', 6], ['runestone', 1]], density: 0.22 },
  taiga: { small: [['pine', 10], ['dead', 1]], density: 0.62 },
  moor: { small: [['dead', 4], ['runestone', 1], ['reeds', 2]], density: 0.2, liquid: 'puddle' },
  crag: { big: [['mtn', 1]], bigDensity: 0.75, small: [['pine', 3], ['dead', 1]], density: 0.18 },
  fjord: { small: [['pine', 8], ['longhouse', 1]], density: 0.34, liquid: 'lake' },
  fields: { small: [['fir', 4], ['longhouse', 2], ['oak', 2]], density: 0.2 },
  ember: { big: [['volcano', 1]], bigDensity: 0.5, small: [['dead', 3], ['spire', 2]], density: 0.2, liquid: 'lava' },
  glacier: { big: [['ice', 1]], bigDensity: 0.7, small: [['pine', 1]], density: 0.06 },
  meadow: { small: [['olive', 6], ['cypress', 2], ['house', 1]], density: 0.3 },
  coast: { small: [['olive', 3], ['cypress', 2], ['house', 2], ['column', 1]], density: 0.2 },
  ash: { big: [['volcano', 1]], bigDensity: 0.45, small: [['dead', 4], ['spire', 1]], density: 0.2, liquid: 'lava' },
  marsh: { small: [['reeds', 5], ['cypress', 2], ['dead', 1]], density: 0.36, liquid: 'puddle' },
  ruins: { big: [['temple', 1]], bigDensity: 0.25, small: [['column', 4], ['broken', 4], ['cypress', 2]], density: 0.3 },
  underworld: { small: [['spire', 4], ['dead', 2], ['grave', 1]], density: 0.3, liquid: 'river' },
  highland: { big: [['hill', 1], ['mtn', 1]], bigDensity: 0.55, small: [['cypress', 3], ['olive', 2]], density: 0.22 },
  bamboo: { small: [['bamboo', 10], ['lantern', 1]], density: 0.45 },
  shrine: { small: [['sakura', 6], ['torii', 1], ['lantern', 2], ['pagoda', 1]], density: 0.3 },
  river: { small: [['bamboo', 4], ['sakura', 2], ['reeds', 2]], density: 0.34, liquid: 'river' },
  graveyard: { small: [['grave', 5], ['dead', 3], ['lantern', 1]], density: 0.36 },
  peak: { big: [['mtn', 1]], bigDensity: 0.75, small: [['fir', 3], ['sakura', 1]], density: 0.2 },
  village: { small: [['pagoda', 4], ['sakura', 3], ['lantern', 2], ['bamboo', 2]], density: 0.36 },
};

function pick<T>(list: [T, number][], u: number): T {
  const total = list.reduce((a, [, w]) => a + w, 0);
  let t = u * total;
  for (const [k, w] of list) { if ((t -= w) < 0) return k; }
  return list[list.length - 1][0];
}

/** 3-4 chamfer distance (in thirds of a pixel) from the cells where mask is 1. */
function chamfer(mask: Uint8Array, W: number, H: number): Uint16Array {
  const d = new Uint16Array(W * H).fill(65000);
  for (let i = 0; i < W * H; i++) if (mask[i]) d[i] = 0;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x; let v = d[i];
    if (x > 0) v = Math.min(v, d[i - 1] + 3);
    if (y > 0) { v = Math.min(v, d[i - W] + 3); if (x > 0) v = Math.min(v, d[i - W - 1] + 4); if (x < W - 1) v = Math.min(v, d[i - W + 1] + 4); }
    d[i] = v;
  }
  for (let y = H - 1; y >= 0; y--) for (let x = W - 1; x >= 0; x--) {
    const i = y * W + x; let v = d[i];
    if (x < W - 1) v = Math.min(v, d[i + 1] + 3);
    if (y < H - 1) { v = Math.min(v, d[i + W] + 3); if (x < W - 1) v = Math.min(v, d[i + W + 1] + 4); if (x > 0) v = Math.min(v, d[i + W - 1] + 4); }
    d[i] = v;
  }
  return d;
}

/** A region's area points (0..1 of the map box) in trail order, as the map page places its nodes. */
export function regionPoints(region: string): MapPoint[] {
  return AREAS.filter((a) => a.region === region).map((a) => ({ id: a.id, x: a.mx, y: (a.my * 330 + 10) / 350 }));
}

/**
 * The painted trail through a region's areas, in map pixels for a W x H map: each leg bends a little to one side so
 * it reads as a road, not a ruler line. `at[k]` is the index in `path` where area k sits.
 */
export function regionTrail(region: string, points: MapPoint[], W: number, H: number): { path: { x: number; y: number }[]; at: number[] } {
  const seed = hashString('map:' + region) % 100003;
  const nodes = points.map((p) => ({ x: p.x * W, y: p.y * H }));
  const path: { x: number; y: number }[] = [], at: number[] = [];
  nodes.forEach((n, i) => {
    if (i > 0) {
      const a = nodes[i - 1], mx = (a.x + n.x) / 2, my = (a.y + n.y) / 2, len = Math.hypot(n.x - a.x, n.y - a.y);
      const bend = (hash(seed, i, 7) - 0.5) * 0.35 * len;
      const cx = mx - ((n.y - a.y) / len) * bend, cy = my + ((n.x - a.x) / len) * bend;
      const steps = Math.max(2, Math.ceil(len / 3));
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        path.push({ x: (1 - t) * (1 - t) * a.x + 2 * (1 - t) * t * cx + t * t * n.x, y: (1 - t) * (1 - t) * a.y + 2 * (1 - t) * t * cy + t * t * n.y });
      }
    } else path.push({ x: n.x, y: n.y });
    at.push(path.length - 1);
  });
  return { path, at };
}

/** Paints a region map W x H map pixels. Points are 0..1 of the box, in trail order. */
export function paintRegionMap(region: string, points: MapPoint[], W: number, H: number): HTMLCanvasElement {
  const seed = hashString('map:' + region) % 100003;
  const N = W * H;
  const nodes = points.map((p) => ({ x: p.x * W, y: p.y * H, biome: biomeFor(p.id).id }));
  const trail = regionTrail(region, points, W, H).path;
  // Distance to the trail: rasterise it, then one chamfer pass (thirds of a pixel).
  const trailMask = new Uint8Array(N);
  for (let i = 1; i < trail.length; i++) {
    const a = trail[i - 1], b = trail[i], n = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) * 2) + 1;
    for (let s = 0; s <= n; s++) {
      const x = Math.round(a.x + ((b.x - a.x) * s) / n), y = Math.round(a.y + ((b.y - a.y) * s) / n);
      if (x >= 0 && y >= 0 && x < W && y < H) trailMask[y * W + x] = 1;
    }
  }
  const trailD = chamfer(trailMask, W, H);

  // ---- land, biome cells, liquids ----
  const land = new Uint8Array(N), cellOf = new Uint8Array(N), cell2 = new Uint8Array(N), edge = new Float32Array(N);
  const nodeD = new Float32Array(N), pathD = new Float32Array(N);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x, nx = x / W, ny = y / H;
    const ex = Math.max(0, Math.abs(nx - 0.5) - 0.36) / 0.12, ey = Math.max(0, Math.abs(ny - 0.5) - 0.35) / 0.12;
    let f = 0.5 - Math.hypot(ex, ey) * 0.7;
    f += (fbm(seed, x / 30, y / 30) - 0.5) * 1.15 + (vnoise(seed + 5, x / 8, y / 8) - 0.5) * 0.28;
    let nd = 1e9, n1 = 0, n2 = 0, d1 = 1e9, d2 = 1e9;
    const wx = x + (vnoise(seed + 51, x / 16, y / 16) - 0.5) * 22, wy = y + (vnoise(seed + 52, x / 16, y / 16) - 0.5) * 22;
    nodes.forEach((n, k) => {
      const d = Math.hypot(x - n.x, y - n.y);
      if (d < nd) nd = d;
      const dw = Math.hypot(wx - n.x, wy - n.y);
      if (dw < d1) { d2 = d1; n2 = n1; d1 = dw; n1 = k; } else if (dw < d2) { d2 = dw; n2 = k; }
    });
    const pd = trailD[i] / 3;
    nodeD[i] = nd; pathD[i] = pd;
    f = Math.max(f, 0.9 - nd / 15, 0.55 - pd / 8);
    if (f <= 0 && region !== 'midgard' && fbm(seed + 11, x / 17, y / 17) + f * 0.25 > 0.69) f = 0.01; // islets
    land[i] = f > 0 ? 1 : 0;
    cellOf[i] = n1; cell2[i] = n2; edge[i] = d2 - d1;
  }
  // inland liquids per biome
  const liquid = new Uint8Array(N); // 1 water, 2 lava
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x;
    if (!land[i] || nodeD[i] < 11 || pathD[i] < 3.5) continue;
    const kind = RECIPE[nodes[cellOf[i]].biome]?.liquid;
    if (!kind) continue;
    if (kind === 'lake' && vnoise(seed + 31, x / 11, y / 11) > 0.74) liquid[i] = 1;
    else if (kind === 'puddle' && vnoise(seed + 33, x / 4.5, y / 4.5) > 0.78 && vnoise(seed + 34, x / 20, y / 20) > 0.45) liquid[i] = 1;
    else if (kind === 'river' && Math.abs(vnoise(seed + 37, x / 15, y / 15) - 0.5) < 0.03) liquid[i] = 1;
    else if (kind === 'lava' && Math.abs(vnoise(seed + 39, x / 13, y / 13) - 0.5) < 0.016 && vnoise(seed + 40, x / 9, y / 9) > 0.42) liquid[i] = 2;
  }
  const sea = new Uint8Array(N), ground = new Uint8Array(N);
  for (let i = 0; i < N; i++) { sea[i] = land[i] ? 0 : 1; ground[i] = land[i] && !liquid[i] ? 1 : 0; }
  const dSea = chamfer(sea, W, H), dLand = chamfer(land, W, H), dWet = chamfer(liquid, W, H);

  // ---- ground, shore, sea ----
  const img = new ImageData(W, H), P = img.data;
  const put = (i: number, c: RGB) => { P[i * 4] = c[0]; P[i * 4 + 1] = c[1]; P[i * 4 + 2] = c[2]; P[i * 4 + 3] = 255; };
  const seaR = (SEA[region] ?? SEA.midgard).map(hex), shore = (SHORE[region] ?? SHORE.midgard).map(hex);
  const looks = nodes.map((n) => biomeLook(n.biome));
  const ramps = looks.map((l) => l.ground.map(hex));
  const liquids = looks.map((l) => l.liquid.map(hex));
  const lavaR = R.lava.map(hex);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x, b = bayer(x, y);
    if (!land[i]) {
      const d = dLand[i] / 3;
      let c: RGB;
      if (d <= 1) c = seaR[4];
      else if (d <= 2.5) c = b < 0.5 ? seaR[3] : seaR[4];
      else if (d < 5 + b * 3) c = seaR[3];
      else if (d < 12 + b * 5) c = seaR[2];
      else c = seaR[1];
      // wave ticks out at sea
      if (d > 7 && hash(seed + 3, x >> 3, y >> 2) < 0.12 && (y & 3) === 0 && ((x & 7) < 3)) c = seaR[3];
      put(i, c); continue;
    }
    const k = edge[i] < 1.6 && hash(seed + 9, x, y) < 0.5 ? cell2[i] : cellOf[i];
    if (liquid[i]) {
      const L = liquid[i] === 2 ? lavaR : liquids[k];
      // edge glow or foam where the liquid meets ground
      let near = false;
      for (const o of [-1, 1, -W, W]) if (ground[i + o]) near = true;
      put(i, liquid[i] === 2 ? (near ? L[4] : L[2 + ((x + y) & 1)]) : near ? L[3] : L[2]);
      continue;
    }
    const ds = dSea[i] / 3;
    if (ds <= 1) { put(i, mixc(seaR[0], shore[0], 0.35)); continue; }
    if (ds <= 2.5 + b) { put(i, shore[ds <= 2 ? 0 : 1]); continue; }
    const G = ramps[k], snowy = looks[k].style === 'snow';
    const n = fbm(seed + 61, x / 22, y / 22) + (vnoise(seed + 62, x / 5, y / 5) - 0.5) * 0.12;
    const base = snowy ? 3 : 2;
    let idx = n > 0.62 + b * 0.06 ? base + 1 : n < 0.36 - b * 0.06 ? base - 1 : base;
    // ground darkens next to liquids, a bank
    let wet = false;
    for (const o of [-1, 1, -W, W]) if (liquid[i + o]) wet = true;
    if (wet) idx = Math.max(0, idx - 2);
    put(i, G[Math.min(4, idx)]);
  }

  // ---- the trail: a worn path, two pixels wide, with lighter stones ----
  const pathC = region === 'midgard' ? ['#5a6488', '#8e98bc'] : region === 'hellas' ? ['#8e6c48', '#c4a070'] : ['#6e5c4c', '#a08a70'];
  const pc = pathC.map(hex);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x;
    if (!ground[i] || nodeD[i] < 7) continue;
    const pd = pathD[i];
    if (pd < 1.1) put(i, (x * 7 + y * 3) % 5 === 0 ? pc[1] : pc[0]);
    else if (pd < 1.6 && bayer(x, y) < 0.5) put(i, pc[0]);
  }
  // clearings: a lighter ring of trodden ground round every area
  nodes.forEach((n, k) => {
    const G = ramps[k], snowy = looks[k].style === 'snow';
    for (let y = Math.floor(n.y - 9); y <= n.y + 9; y++) for (let x = Math.floor(n.x - 9); x <= n.x + 9; x++) {
      if (x < 0 || y < 0 || x >= W || y >= H) continue;
      const i = y * W + x, d = Math.hypot(x - n.x, y - n.y);
      if (!ground[i] || dSea[i] < 9) continue;
      if (d < 7.5 + bayer(x, y) * 1.5) put(i, G[snowy ? 4 : 3]);
    }
  });

  // ---- landmarks ----
  const placed: { s: Spr; x: number; y: number }[] = [];
  const taken = new Uint8Array(N);
  const free = (x: number, y: number, w: number, h: number, pad: number) => {
    for (let yy = y - h - pad; yy <= y + pad; yy++) for (let xx = x - pad; xx < x + w + pad; xx++) {
      if (xx < 0 || yy < 0 || xx >= W || yy >= H) return false;
      if (taken[yy * W + xx]) return false;
    }
    return true;
  };
  const occupy = (x: number, y: number, w: number, h: number) => {
    for (let yy = y - h; yy <= y; yy++) for (let xx = x; xx < x + w; xx++) if (xx >= 0 && yy >= 0 && xx < W && yy < H) taken[yy * W + xx] = 1;
  };
  const okBase = (x: number, y: number, w: number) => {
    for (let xx = x; xx < x + w; xx++) {
      const i = y * W + xx;
      if (xx < 0 || xx >= W || y >= H || y < 0) return false;
      if (!ground[i] || dSea[i] < 7 || dWet[i] < 4 || nodeD[i] < 13 || pathD[i] < 3.5) return false;
    }
    return true;
  };
  // an area's own landmarks first, round its token: beside or above it, never below where its label hangs
  points.forEach((p, k) => {
    const make = AREA_MARKS[p.id];
    if (!make) return;
    const r = rng(hashString('mark:' + p.id));
    for (const s of make(r)) {
      for (let a = 0; a < 24; a++) {
        const ang = Math.PI * (0.95 + (a % 12) * 0.1) + (a >= 12 ? 0.05 : 0), rad = (a >= 12 ? 22 : 17) + s.w / 2;
        const x = Math.round(nodes[k].x + Math.cos(ang) * rad - s.w / 2), y = Math.round(nodes[k].y + Math.sin(ang) * rad * 0.8 + s.h / 2);
        if (!okBase(x, y, s.w) || !free(x, y, s.w, s.h, 1)) continue;
        occupy(x, y, s.w, s.h);
        placed.push({ s, x, y });
        break;
      }
    }
  });
  for (const pass of ['big', 'small'] as const) {
    const step = pass === 'big' ? 9 : 4;
    for (let gy = 0; gy < H; gy += step) for (let gx = 0; gx < W; gx += step) {
      const x = Math.floor(gx + hash(seed + 71, gx, gy) * step), y = Math.floor(gy + hash(seed + 72, gx, gy) * step);
      if (x >= W || y >= H || !land[y * W + x]) continue;
      const k = cellOf[y * W + x], rec = RECIPE[nodes[k].biome];
      if (!rec) continue;
      const list = pass === 'big' ? rec.big : rec.small;
      const dens = pass === 'big' ? rec.bigDensity ?? 0 : rec.density;
      if (!list || !dens) continue;
      const clump = pass === 'small' ? fbm(seed + 81, x / 14, y / 14) * 1.6 - 0.3 : 1;
      if (hash(seed + 73, gx, gy) > dens * clump) continue;
      const r = rng(Math.floor(hash(seed + 74, x, y) * 1e9));
      const s = makeSprite(pick(list, r()), r, looks[k].ground);
      const sx = x - (s.w >> 1);
      if (!okBase(sx, y, s.w) || !free(sx, y, s.w, s.h, pass === 'big' ? 1 : 0)) continue;
      occupy(sx, y, s.w, Math.max(2, s.h >> 1));
      placed.push({ s, x: sx, y });
    }
  }
  placed.sort((a, b) => a.y - b.y);
  const ol = hex(STYLE.outline);
  for (const { s, x, y } of placed) {
    const top = y - s.h + 1;
    // a short hard shadow to the lower right
    for (let xx = 1; xx <= s.w; xx++) {
      const i = (y + 1) * W + x + xx;
      if (y + 1 < H && x + xx < W && ground[i]) put(i, mixc([P[i * 4], P[i * 4 + 1], P[i * 4 + 2]], ol, 0.35));
    }
    const filled = (sx: number, sy: number) => sx >= 0 && sy >= 0 && sx < s.w && sy < s.h && s.px[sy * s.w + sx] > 0;
    for (let sy = -1; sy <= s.h; sy++) for (let sx = -1; sx <= s.w; sx++) {
      const X = x + sx, Y = top + sy;
      if (X < 0 || Y < 0 || X >= W || Y >= H) continue;
      if (filled(sx, sy)) put(Y * W + X, s.pal[s.px[sy * s.w + sx]]);
      else if (filled(sx - 1, sy) || filled(sx + 1, sy) || filled(sx, sy - 1) || filled(sx, sy + 1)) put(Y * W + X, ol);
    }
  }

  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  c.getContext('2d')!.putImageData(img, 0, 0);
  return c;
}

const cache = new Map<string, string>();
/** A data URL of the painted region map for a map box of cssW x cssH, cached per region and size. */
export function regionMapUrl(region: string, points: MapPoint[], cssW: number, cssH: number): string {
  const W = Math.max(90, Math.round(cssW / MAP_SCALE)), H = Math.max(80, Math.round(cssH / MAP_SCALE));
  const key = `${region}:${W}x${H}`;
  let url = cache.get(key);
  if (!url) { url = paintRegionMap(region, points, W, H).toDataURL(); cache.set(key, url); }
  return url;
}
